import classNames from 'classnames';
import { FC } from 'react';

import CloseX from '../svgs/CloseX';

import Button from './Button';
import UpdateButton from './UpdateButton';

import { FilterIcon, XIcon } from '@heroicons/react/outline';
import InputGroup from './InputGroup';
import Search from '../svgs/Search';
import SelectMenu from './SelectMenu';
import { contentTypeFilterValues } from './Library/helpers';
import { ContentType } from '../lib/followUp';
import {
  PATIENT_LIBRARY_STATUS_OPTIONS,
  PatientLibraryStatusFilter,
} from '../pages/ClientPortal/PatientLibrary/filterParams';

const ContentTypeFilterOption: FC<{ value: ContentType }> = ({ value }) => {
  const label = [
    ContentType.PDF,
    ContentType.PdfForm,
    ContentType.Esign,
  ].includes(value)
    ? 'File'
    : value;
  return <>{label}</>;
};

const SearchAndFilters: FC<{
  searchTermValue: string;
  clearSearchTerm: () => void;
  clearFilterTerm: () => void;
  onChangeSearch: (e: React.ChangeEvent<HTMLInputElement>) => void;
  contentTypeFilter: ContentType | null;
  setContentTypeFilter: (value: ContentType | null) => void;
  contentTypeFilterLock?: ContentType;
  patientMode?: boolean;
  statusFilter?: PatientLibraryStatusFilter | null;
  setStatusFilter?: (value: PatientLibraryStatusFilter | null) => void;
}> = ({
  searchTermValue,
  clearSearchTerm,
  clearFilterTerm,
  onChangeSearch,
  contentTypeFilter,
  setContentTypeFilter,
  contentTypeFilterLock,
  patientMode = false,
  statusFilter = null,
  setStatusFilter,
}) => {
  const showStatusFilter = Boolean(setStatusFilter);
  const activeFilterCount =
    Number(Boolean(contentTypeFilter)) + Number(Boolean(statusFilter));
  const hasActiveFilters = activeFilterCount > 0;

  const typeSelect = (
    <SelectMenu
      fieldValue={contentTypeFilter}
      placeholder="Filter by type..."
      label=""
      hideLabel
      fieldOptions={contentTypeFilterValues(patientMode)}
      onChange={(value) => setContentTypeFilter(value as ContentType)}
      buttonClassName="!py-2"
      containerClassName={patientMode ? 'w-[230px]' : 'w-[160px]'}
      selectedPrefix={patientMode ? 'Type:' : undefined}
      onClear={
        patientMode && contentTypeFilter
          ? () => setContentTypeFilter(null)
          : undefined
      }
      SelectOptionComponent={ContentTypeFilterOption}
    />
  );

  const statusSelect = showStatusFilter ? (
    <SelectMenu
      fieldValue={statusFilter}
      placeholder="Filter by status..."
      label=""
      hideLabel
      fieldOptions={PATIENT_LIBRARY_STATUS_OPTIONS}
      onChange={(value) =>
        setStatusFilter?.(value as PatientLibraryStatusFilter)
      }
      buttonClassName="!py-2"
      containerClassName="w-[250px]"
      selectedPrefix="Status:"
      onClear={statusFilter ? () => setStatusFilter?.(null) : undefined}
    />
  ) : null;

  return (
    <div
      className={classNames(
        'flex',
        patientMode
          ? 'w-full flex-col items-stretch gap-3 md:flex-row md:flex-wrap md:items-center'
          : 'h-12 flex-row items-center justify-center',
      )}
    >
      <InputGroup
        labelHidden
        label="Search"
        placeholder="Search..."
        className={classNames(
          'w-80 rounded-full pl-12 disabled:cursor-not-allowed',
        )}
        inputSize="extra-small"
        containerClassName="mr-4"
        iconLeftClassName="text-neutral-125 ml-2"
        IconLeft={Search}
        IconRight={searchTermValue.length > 0 ? CloseX : null}
        iconRightOnClick={clearSearchTerm}
        iconRightClassName="text-neutral-125 mr-1"
        value={searchTermValue}
        onChange={onChangeSearch}
      />
      {!contentTypeFilterLock &&
        (patientMode ? (
          <div className="flex min-w-0 flex-row flex-wrap items-center gap-x-3 gap-y-2">
            <div className="flex flex-row items-center gap-1.5">
              <FilterIcon className="h-4 w-4 flex-shrink-0 text-neutral-125" />
              <span className="text-caption font-medium text-neutral-125">
                Filters:
              </span>
            </div>
            <div className="relative z-[100] flex flex-row flex-wrap items-center gap-2">
              {typeSelect}
              {statusSelect}
              {activeFilterCount > 0 && (
                <UpdateButton
                  onClick={clearFilterTerm}
                  IconComponent={XIcon}
                  theme="red"
                  text={
                    activeFilterCount > 1 ? 'Clear filters' : 'Clear filter'
                  }
                  ariaLabel={
                    activeFilterCount > 1 ? 'Clear filters' : 'Clear filter'
                  }
                  className="mr-0"
                />
              )}
            </div>
          </div>
        ) : (
          <div className="flex flex-row items-center justify-start gap-2">
            <div
              className={classNames(
                'relative z-10 transition',
                !hasActiveFilters && 'pointer-events-none opacity-0',
                hasActiveFilters && 'opacity-100',
              )}
            >
              <Button
                className="h-[24px] w-[24px] rounded-full"
                iconClassName="min-h-[14px] min-w-[14px] ml-0"
                size="extra-small"
                theme="secondary"
                aria-label="Clear filters"
                IconComponent={XIcon}
                onClick={clearFilterTerm}
              />
            </div>
            <div
              className={classNames(
                'relative z-[100] flex flex-row items-center justify-start gap-2 transition',
                !hasActiveFilters && '-translate-x-6',
                hasActiveFilters && 'translate-x-[10px]',
              )}
            >
              {typeSelect}
            </div>
          </div>
        ))}
    </div>
  );
};

export default SearchAndFilters;
